import { ReactNode, useState, useMemo, useRef, useEffect, createContext, useContext } from "react";
import { useRegistrarPainelIphone } from "@/components/AvisoDoIphone";
import { isPaidPlanId } from "@/lib/plans";
import { createPortal } from "react-dom";
import { useAuth } from "@/hooks/use-auth";
import { readAccess, canAccessPage, pageKeyForPath } from "@/lib/access";

// "Sair" dentro de "Minhas lojas". Existe porque o único logout do painel
// morava no fim de Configurações — e quem tem acesso restrito (Equipe) pode
// não ter essa página. Fica pra todo mundo: é onde se espera achar a conta.
function SairButton({ onBefore }: { onBefore: () => void }) {
  const { user, logout } = useAuth();
  return (
    <button
      type="button"
      onClick={() => { onBefore(); if (user) logout(); }}
      className="px-3 py-2.5 hover:bg-destructive/10 transition-colors flex items-center gap-2 text-destructive shrink-0"
      aria-label="Sair da conta"
    >
      <LogOut className="w-4 h-4" />
      <span className="text-sm font-medium">Sair</span>
    </button>
  );
}
import { useStoreColors } from "@/hooks/use-store-colors";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Wallet,
  Users,
  Settings,
  Store as StoreIcon,
  Calculator,
  UtensilsCrossed,
  Banknote,
  Scissors,
  CalendarDays,
  UserRound,
  MoreHorizontal,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  Plus,
  MapPin,
  Check,
  Building2,
  ArrowLeft,
  Loader2,
  Trash2,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { BrandTile } from "@/components/BrandTile";
import { useSwitchUnit, useListProducts, useGetStore, customFetch, storeSessionId, getGetMeQueryKey } from "@workspace/api-client-react";
import { useFeatureAllowed } from "@/hooks/use-plan";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { CreateStoreModal } from "@/components/CreateStoreModal";
import { useAutoPrintWatcher } from "@/hooks/use-auto-print-watcher";
import { useNewOrderSound } from "@/hooks/use-new-order-sound";
import { FaixaLojaPausada } from "@/components/admin/FaixaLojaPausada";
import { useOrdersRealtime } from "@/hooks/use-orders-realtime";

const AllUnitsContext = createContext(false);
export function useAllUnitsMode() {
  return useContext(AllUnitsContext);
}

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
  showFor: string[];
  // Só aparece quando a loja ativou este opt-in (flag em stores).
  optIn?: "cashRegister" | "comandas";
}

const allNavItems: NavItem[] = [
  { label: "Dashboard", href: "/", icon: LayoutDashboard, showFor: ["produtos", "servicos", "ambos"] },
  { label: "Produtos", href: "/produtos", icon: Package, showFor: ["produtos", "ambos"] },
  { label: "Serviços", href: "/servicos", icon: Scissors, showFor: ["servicos", "ambos"] },
  { label: "Pedidos", href: "/pedidos", icon: ShoppingBag, showFor: ["produtos", "ambos"] },
  { label: "PDV", href: "/pdv", icon: Calculator, showFor: ["produtos", "ambos"] },
  // Comandas/Mesas (Pedro, 30/10/2026): só no Ultra com o pedido na mesa ligado; o nome segue a chave.
  { label: "Comandas", href: "/comandas", icon: UtensilsCrossed, showFor: ["produtos", "ambos"], optIn: "comandas" },
  { label: "Caixa", href: "/caixa", icon: Banknote, showFor: ["produtos", "servicos", "ambos"], optIn: "cashRegister" },
  { label: "Agenda", href: "/agenda", icon: CalendarDays, showFor: ["servicos", "ambos"] },
  { label: "Profissionais", href: "/profissionais", icon: UserRound, showFor: ["servicos", "ambos"] },
  { label: "Financeiro", href: "/financeiro", icon: Wallet, showFor: ["produtos", "servicos", "ambos"] },
  { label: "Clientes", href: "/clientes", icon: Users, showFor: ["produtos", "servicos", "ambos"] },
  { label: "Configurações", href: "/configuracoes", icon: Settings, showFor: ["produtos", "servicos", "ambos"] },
];

// Ordem da nav mobile (bottom bar). A ordem aqui define a ordem renderizada
// — diferente da sidebar do desktop que segue allNavItems. Itens fora dessa
// lista vão pro menu "Mais". `mobileMainTabsOverflow` lista as tabs que são
// "primeiro a sair" quando a tela é estreita: ficam visíveis acima de 420px
// e caem no menu "Mais" abaixo.
const mobileMainTabsAlways = ["Dashboard", "Pedidos", "Agenda", "Produtos"];
const mobileMainTabsOverflow = ["Financeiro"];
const mobileMainTabs = [...mobileMainTabsAlways, ...mobileMainTabsOverflow];

const ALL_UNITS_ID = "__all_units__";

function UnitSwitcher({ units, currentStoreId, storeName, logoUrl, slug, isPro, allUnitsMode, onToggleAllUnits }: {
  units: Array<{ id: string; name: string; address?: string | null; isParent: boolean; active?: boolean; productCount?: number }>;
  currentStoreId: string;
  storeName: string;
  logoUrl?: string | null;
  slug: string;
  isPro: boolean;
  allUnitsMode: boolean;
  onToggleAllUnits: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const queryClient = useQueryClient();

  const switchMutation = useSwitchUnit({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries();
        setOpen(false);
      },
    },
  });

  async function handleDeleteUnit() {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    try {
      await customFetch(`/api/units/${deleteConfirm.id}`, { method: "DELETE" });
      queryClient.invalidateQueries();
      setDeleteConfirm(null);
    } catch (err: any) {
      const msg = err?.message || "Não foi possível excluir.";
      // Toast inline minimo — sem dependency extra
      alert(msg);
    } finally {
      setDeleting(false);
    }
  }

  const hasMultipleUnits = units.length > 1;
  // Grupo matriz↔filial legado (some com a migração). Badge "Matriz" e o modo
  // "Todas as unidades" só fazem sentido pra grupos de verdade — lojas
  // independentes (conta multi-loja) não ganham badge.
  const hasChildUnits = units.some((u) => !u.isParent);
  const showDropdownTrigger = true;

  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 240),
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSwitch = (unitId: string) => {
    if (unitId === ALL_UNITS_ID) {
      setOpen(false);
      onToggleAllUnits();
      return;
    }
    if (allUnitsMode) {
      onToggleAllUnits();
    }
    if (unitId === currentStoreId && !allUnitsMode) {
      setOpen(false);
      return;
    }
    switchMutation.mutate({ data: { unitId } });
  };

  const displayName = allUnitsMode ? "Todas as unidades" : storeName;

  return (
    <>
      <div className="p-6 flex items-center gap-3 border-b border-border/50">
        {/* Slot do logo: 3 estados.
            1) All-units mode: ícone Building2 sobre gradiente sec→primary (multi-loja, conceito de grupo).
            2) Loja com logoUrl: a imagem da PRÓPRIA loja (não tocar).
            3) Loja sem logo: BrandTile institucional do Venda no Zap. */}
        {allUnitsMode ? (
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
            <Building2 className="text-white w-5 h-5" />
          </div>
        ) : logoUrl ? (
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
            <img src={logoUrl} alt={storeName} className="w-full h-full object-cover" />
          </div>
        ) : (
          <BrandTile size="sm" className="rounded-xl w-10 h-10 shadow-md" />
        )}
        <div className="flex-1 min-w-0">
          {showDropdownTrigger ? (
            <button
              ref={triggerRef}
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1 hover:bg-muted/50 rounded-md px-1 -mx-1 py-0.5 transition-colors w-full"
            >
              <h2 className="font-bold text-lg leading-tight truncate">{displayName}</h2>
            </button>
          ) : (
            <h2 className="font-bold text-lg leading-tight truncate w-40">{displayName}</h2>
          )}
          {!allUnitsMode && <p className="text-xs text-muted-foreground truncate pl-[2px]">{slug}</p>}
          {/* O caret "˅" passa despercebido — botão explícito, mesma ação. */}
          <button
            onClick={() => setOpen(!open)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <ArrowLeftRight className="w-3 h-3" /> Trocar loja
          </button>
        </div>
      </div>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[100] bg-card rounded-xl border shadow-xl py-1 max-h-80 overflow-y-auto"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {hasChildUnits && (
            <>
              <button
                onClick={() => handleSwitch(ALL_UNITS_ID)}
                className={cn(
                  "w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2",
                  allUnitsMode && "bg-accent/10",
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium">Todas as unidades</span>
                  </div>
                </div>
                {allUnitsMode && (
                  <Check className="w-4 h-4 text-accent flex-shrink-0" />
                )}
              </button>
              <div className="border-t border-border/50 my-1" />
            </>
          )}
          {units.map((unit) => {
            const isCurrent = unit.id === currentStoreId && !allUnitsMode;
            // Pós multi-loja: qualquer loja adicional pode ser excluída (o
            // servidor protege a principal e a selecionada).
            const canDelete = !isCurrent && units.length > 1;
            return (
              <div
                key={unit.id}
                className={cn(
                  "w-full px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2",
                  isCurrent && "bg-accent/10",
                )}
              >
                <button
                  onClick={() => handleSwitch(unit.id)}
                  disabled={switchMutation.isPending}
                  className="flex-1 min-w-0 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-1.5">
                    <span className={cn("text-sm font-medium truncate", unit.active === false && "text-muted-foreground")}>{unit.name}</span>
                    {unit.isParent && hasChildUnits && (
                      <span className="text-[10px] bg-secondary/20 text-secondary px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                        Matriz
                      </span>
                    )}
                    {unit.active === false && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                        Desativada{unit.productCount ? ` · ${unit.productCount} ${unit.productCount === 1 ? "produto" : "produtos"}` : ""}
                      </span>
                    )}
                  </div>
                  {unit.address && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">{unit.address}</span>
                    </div>
                  )}
                </button>
                {isCurrent && (
                  <Check className="w-4 h-4 text-accent flex-shrink-0" />
                )}
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({ id: unit.id, name: unit.name });
                    }}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 cursor-pointer"
                    title="Excluir loja"
                    disabled={deleting}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          <>
            <div className="border-t border-border/50 my-1" />
            <div className="flex items-center justify-between">
              <button
                onClick={() => { setOpen(false); setCreateModalOpen(true); }}
                className="flex-1 text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2 text-primary"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Adicionar loja</span>
              </button>
              <SairButton onBefore={() => setOpen(false)} />
            </div>
          </>
        </div>,
        document.body
      )}

      <CreateStoreModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(storeId) => {
          setCreateModalOpen(false);
          switchMutation.mutate({ data: { unitId: storeId } });
        }}
      />

      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              <h3 className="font-semibold text-lg">Excluir loja?</h3>
            </div>
            <p className="text-sm">
              Vai apagar permanentemente <span className="font-semibold">"{deleteConfirm.name}"</span> e
              todos os dados ligados a ela: produtos, pedidos, clientes, financeiro, agendamentos.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ Esta ação não pode ser desfeita.
            </p>
            {/* Empilha no mobile (como o AlertDialogFooter): lado a lado os
                dois não cabiam em 390px e o Cancelar vazava do card. */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteUnit}
                disabled={deleting}
              >
                {deleting ? "Excluindo…" : "Excluir permanentemente"}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function MobileUnitSwitcher({ units, currentStoreId, storeName, logoUrl, isPro, allUnitsMode, onToggleAllUnits }: {
  units: Array<{ id: string; name: string; address?: string | null; isParent: boolean; active?: boolean; productCount?: number }>;
  currentStoreId: string;
  storeName: string;
  logoUrl?: string | null;
  isPro: boolean;
  allUnitsMode: boolean;
  onToggleAllUnits: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const queryClient = useQueryClient();

  const switchMutation = useSwitchUnit({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries();
        setOpen(false);
      },
    },
  });

  async function handleDeleteUnit() {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    try {
      await customFetch(`/api/units/${deleteConfirm.id}`, { method: "DELETE" });
      queryClient.invalidateQueries();
      setDeleteConfirm(null);
    } catch (err: any) {
      alert(err?.message || "Não foi possível excluir.");
    } finally {
      setDeleting(false);
    }
  }

  const hasMultipleUnits = units.length > 1;
  // Grupo matriz↔filial legado (some com a migração). Badge "Matriz" e o modo
  // "Todas as unidades" só fazem sentido pra grupos de verdade — lojas
  // independentes (conta multi-loja) não ganham badge.
  const hasChildUnits = units.some((u) => !u.isParent);
  const showDropdownTrigger = true;

  const handleSwitch = (unitId: string) => {
    if (unitId === ALL_UNITS_ID) {
      setOpen(false);
      onToggleAllUnits();
      return;
    }
    if (allUnitsMode) {
      onToggleAllUnits();
    }
    if (unitId === currentStoreId && !allUnitsMode) {
      setOpen(false);
      return;
    }
    switchMutation.mutate({ data: { unitId } });
  };

  const displayName = allUnitsMode ? "Todas as unidades" : storeName;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Mesma lógica do header mobile da sidebar (acima). */}
        {allUnitsMode ? (
          <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
            <Building2 className="text-white w-3.5 h-3.5" />
          </div>
        ) : logoUrl ? (
          <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0">
            <img src={logoUrl} alt={storeName} className="w-full h-full object-cover" />
          </div>
        ) : (
          <BrandTile size="sm" className="w-7 h-7 rounded-lg" />
        )}
        {showDropdownTrigger ? (
          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-1"
          >
            <span className="font-bold text-sm">{displayName}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        ) : (
          <span className="font-bold text-sm">{displayName}</span>
        )}
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div
            className="absolute top-14 left-4 right-4 bg-card rounded-2xl border shadow-xl p-2 max-h-80 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-2 py-1 mb-1">
              <span className="font-semibold text-sm text-muted-foreground">Minhas lojas</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            {hasChildUnits && (
              <>
                <button
                  onClick={() => handleSwitch(ALL_UNITS_ID)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2",
                    allUnitsMode && "bg-accent/10",
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium">Todas as unidades</span>
                    </div>
                  </div>
                  {allUnitsMode && (
                    <Check className="w-4 h-4 text-accent flex-shrink-0" />
                  )}
                </button>
                <div className="border-t border-border/50 my-1" />
              </>
            )}
            {units.map((unit) => {
              const isCurrent = unit.id === currentStoreId && !allUnitsMode;
              // Pós multi-loja: qualquer loja adicional pode ser excluída (o
            // servidor protege a principal e a selecionada).
            const canDelete = !isCurrent && units.length > 1;
              return (
                <div
                  key={unit.id}
                  className={cn(
                    "w-full px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2",
                    isCurrent && "bg-accent/10",
                  )}
                >
                  <button
                    onClick={() => handleSwitch(unit.id)}
                    disabled={switchMutation.isPending}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={cn("text-sm font-medium truncate", unit.active === false && "text-muted-foreground")}>{unit.name}</span>
                      {unit.isParent && hasChildUnits && (
                        <span className="text-[10px] bg-secondary/20 text-secondary px-1.5 py-0.5 rounded-full font-medium">
                          Matriz
                        </span>
                      )}
                      {unit.active === false && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                          Desativada{unit.productCount ? ` · ${unit.productCount} ${unit.productCount === 1 ? "produto" : "produtos"}` : ""}
                        </span>
                      )}
                    </div>
                    {unit.address && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">{unit.address}</span>
                      </div>
                    )}
                  </button>
                  {isCurrent && (
                    <Check className="w-4 h-4 text-accent flex-shrink-0" />
                  )}
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ id: unit.id, name: unit.name });
                      }}
                      className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 cursor-pointer"
                      title="Excluir loja"
                      disabled={deleting}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            <div className="border-t border-border/50 my-1" />
            <div className="flex items-center justify-between">
              <button
                onClick={() => { setOpen(false); setCreateModalOpen(true); }}
                className="flex-1 text-left px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-2 text-primary"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Adicionar loja</span>
              </button>
              <SairButton onBefore={() => setOpen(false)} />
            </div>
          </div>
        </div>,
        document.body
      )}

      <CreateStoreModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(storeId) => {
          setCreateModalOpen(false);
          switchMutation.mutate({ data: { unitId: storeId } });
        }}
      />

      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              <h3 className="font-semibold text-lg">Excluir loja?</h3>
            </div>
            <p className="text-sm">
              Vai apagar permanentemente <span className="font-semibold">"{deleteConfirm.name}"</span> e
              todos os dados ligados a ela: produtos, pedidos, clientes, financeiro, agendamentos.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ Esta ação não pode ser desfeita.
            </p>
            {/* Empilha no mobile (como o AlertDialogFooter): lado a lado os
                dois não cabiam em 390px e o Cancelar vazava do card. */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteUnit}
                disabled={deleting}
              >
                {deleting ? "Excluindo…" : "Excluir permanentemente"}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function NoStoreFallback() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  useEffect(() => {
    const t = setTimeout(() => {
      storeSessionId(null);
      queryClient.setQueryData(getGetMeQueryKey(), null);
      setLocation("/login");
    }, 4000);
    return () => clearTimeout(t);
  }, [queryClient, setLocation]);
  return (
    <div className="min-h-[100svh] w-full flex items-center justify-center bg-muted/30">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [allUnitsMode, setAllUnitsMode] = useState(false);
  // PDV no computador: menu recolhido (só ícones). Com a tela dividida (~845px), os 256px da barra deixavam ~150px
  // para a lista de produtos (print do Pedro, 11/10/2026). A seta abre o menu inteiro POR CIMA, sem apertar o PDV;
  // o layout remonta a cada página, então entrar no PDV sempre começa recolhido.
  const menuRecolhivel = location === "/pdv";
  const [menuAberto, setMenuAberto] = useState(false);
  const recolhido = menuRecolhivel && !menuAberto;

  useEffect(() => {
    if (!menuAberto) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuAberto(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuAberto]);

  const storeType = user?.store?.storeType || "produtos";

  // A Central de Atendimento não é a tela de loja nenhuma (Pedro, 30/10/2026): impersonando pelo «Entrar na loja» dela, ela
  // fica sem a faixa «Visualizando a loja» e com as cores de antes (as da loja da sessão de suporte, guardadas aqui). A
  // página de tarefas do Pedro (/pedro, 31/10/2026) também.
  const naCentral = location === "/atendimento" || location === "/pedro";
  const impersonando = !!(user as any)?.isImpersonating;
  // Como a loja abre o painel no iPhone, para a Atendente dar o passo certo (T-0007). O suporte entrando na loja não conta.
  useRegistrarPainelIphone(!!user?.store && !impersonando && !naCentral);
  useEffect(() => {
    if (!naCentral || impersonando || !user?.store) return;
    try {
      const { colorPrimary, colorSecondary, colorAccent } = user.store as any;
      localStorage.setItem("central-cores", JSON.stringify({ colorPrimary, colorSecondary, colorAccent }));
    } catch {
      // sem armazenamento: impersonando, a Central fica com as cores padrão
    }
  }, [naCentral, impersonando, user?.store]);
  const coresDaCentral = (() => {
    try {
      return JSON.parse(localStorage.getItem("central-cores") ?? "null");
    } catch {
      return null;
    }
  })();
  useStoreColors(naCentral && impersonando ? coresDaCentral : user?.store);
  useOrdersRealtime();
  useAutoPrintWatcher();
  useNewOrderSound();

  const { data: productsData } = useListProducts({ query: { enabled: storeType === "produtos" } });
  const hasOrderBasedProducts = useMemo(() => {
    if (storeType !== "produtos") return false;
    return (productsData || []).some((p: any) => p.isOrderBased);
  }, [productsData, storeType]);

  const cashRegisterEnabled = !!(user?.store as any)?.cashRegisterEnabled;
  const { data: lojaDoMenu } = useGetStore();
  const podeMesas = useFeatureAllowed("table_orders");
  const comandasLigadas = podeMesas && (lojaDoMenu as any)?.tableOrdersEnabled === true;
  const rotuloComandas = (lojaDoMenu as any)?.tableQrPerClient === true ? "Comandas" : "Mesas";
  // Acesso por página (Configurações → Equipe): membro só vê o que liberaram.
  const access = readAccess(user);
  const navItems = useMemo(
    () => allNavItems.filter((item) => {
      if (!canAccessPage(access, pageKeyForPath(item.href))) return false;
      if (item.optIn === "cashRegister" && !cashRegisterEnabled) return false;
      if (item.optIn === "comandas" && !comandasLigadas) return false;
      if (item.showFor.includes(storeType)) return true;
      if (item.label === "Agenda" && hasOrderBasedProducts) return true;
      return false;
    }).map((item) => (item.optIn === "comandas" ? { ...item, label: rotuloComandas } : item)),
    [storeType, hasOrderBasedProducts, cashRegisterEnabled, comandasLigadas, rotuloComandas, access],
  );

  const mobileBottomItems = useMemo(() => {
    // main segue a ordem do mobileMainTabs (não a do navItems) pra permitir
    // posicionar tabs específicas independente da sidebar do desktop. Ex:
    // "Produtos" entre "Agenda" e "Financeiro" só no mobile.
    const main = mobileMainTabs
      .map((label) => navItems.find((item) => item.label === label))
      .filter((item): item is NavItem => !!item);
    const more = navItems.filter((item) => !mobileMainTabs.includes(item.label) && item.label !== "Dashboard");
    // moreOverflow: tabs que pertencem ao main mas ficam escondidas em telas
    // estreitas; renderizadas no Mais com responsive reverso pra aparecer só
    // < 420px. Evita o aperto visual quando todas as 5 tabs main + Mais
    // tentam caber numa tela de 320-400px.
    const moreOverflow = mobileMainTabsOverflow
      .map((label) => navItems.find((item) => item.label === label))
      .filter((item): item is NavItem => !!item);
    return { main, more, moreOverflow };
  }, [navItems]);

  const handleToggleAllUnits = () => {
    setAllUnitsMode((prev) => {
      if (!prev) {
        setLocation("/");
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (allUnitsMode && location !== "/" && location !== "/configuracoes") {
      setLocation("/");
    }
  }, [allUnitsMode, location, setLocation]);

  if (!user) return null;
  if (!user.store) {
    return <NoStoreFallback />;
  }

  const isActive = (href: string) =>
    location === href || (href !== "/" && location.startsWith(href));

  const units = user.units || [];
  const isPro = isPaidPlanId(user.store.plan);
  const hasMultipleUnits = units.length > 1;
  // Grupo matriz↔filial legado (some com a migração). Badge "Matriz" e o modo
  // "Todas as unidades" só fazem sentido pra grupos de verdade — lojas
  // independentes (conta multi-loja) não ganham badge.
  const hasChildUnits = units.some((u: any) => !u.isParent);
  // O seletor de loja é tela cheia: sem barra lateral, sem nav de baixo.
  // QR codes das mesas: página de impressão, sem menu em volta.
  // Central de Atendimento: a tela inteira para as conversas (e a /pedro, para as tarefas).
  const hideChrome =
    location === "/results" || location === "/lojas" || location === "/closer" || location === "/configuracoes/mesas" || naCentral;

  if (hideChrome) {
    return (
      <AllUnitsContext.Provider value={allUnitsMode}>
        <div className="min-h-[100svh] bg-muted/30 w-full">
          {(user as any).isImpersonating && !naCentral && (
            <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
              <span className="font-medium">Visualizando a loja: {user.store.name}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-amber-600 gap-1.5"
                onClick={async () => {
                  await customFetch("/api/admin/results/stop-impersonate", { method: "POST" });
                  queryClient.invalidateQueries();
                  setLocation("/results");
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar à minha conta
              </Button>
            </div>
          )}
          <main className="w-full">{children}</main>
        </div>
      </AllUnitsContext.Provider>
    );
  }

  return (
    <AllUnitsContext.Provider value={allUnitsMode}>
      <div className="min-h-[100svh] bg-muted/30 flex w-full">
        {menuRecolhivel && menuAberto && (
          <div className="hidden md:block fixed inset-0 z-[15] bg-black/20" onClick={() => setMenuAberto(false)} />
        )}
        <aside className={cn(
          "hidden md:flex flex-col bg-card border-r border-border fixed h-[100svh] inset-y-0 z-20",
          recolhido ? "w-[72px]" : "w-64",
          menuAberto ? "shadow-xl" : "shadow-sm",
        )}>
          {menuRecolhivel && (
            <button
              type="button"
              onClick={() => setMenuAberto((v) => !v)}
              aria-label={menuAberto ? "Recolher menu" : "Abrir menu"}
              className="absolute -right-3 top-6 z-10 w-6 h-6 rounded-full border border-border bg-card shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground"
              data-testid="menu-lateral-seta"
            >
              {menuAberto ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          {recolhido ? (
            <>
              <div className="p-4 flex justify-center border-b border-border/50">
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={() => setMenuAberto(true)} aria-label="Abrir menu" className="rounded-xl">
                      {user.store.logoUrl ? (
                        <div className="w-10 h-10 rounded-xl overflow-hidden shadow-md">
                          <img src={user.store.logoUrl} alt={user.store.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <BrandTile size="sm" className="rounded-xl w-10 h-10 shadow-md" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{user.store.name}</TooltipContent>
                </Tooltip>
              </div>
              <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto" data-testid="menu-lateral-recolhido">
                {navItems.map((item) => (
                  <Tooltip key={item.href} delayDuration={100}>
                    <TooltipTrigger asChild>
                      <Link
                        href={item.href}
                        aria-label={item.label}
                        className={cn(
                          "flex items-center justify-center py-2.5 rounded-lg transition-all duration-200",
                          isActive(item.href)
                            ? "bg-accent/10 border border-accent border-l-4 border-l-accent"
                            : "hover:bg-muted border border-transparent border-l-4 border-l-transparent",
                        )}
                      >
                        <item.icon className={cn("w-5 h-5", isActive(item.href) ? "text-accent" : "text-muted-foreground")} />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                ))}
              </nav>
            </>
          ) : (
            <>
              <UnitSwitcher
                units={units}
                currentStoreId={user.store.id}
                storeName={user.store.name}
                logoUrl={user.store.logoUrl}
                slug={user.store.slug}
                isPro={isPro}
                allUnitsMode={allUnitsMode}
                onToggleAllUnits={handleToggleAllUnits}
              />

              {!allUnitsMode && (
                <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setMenuAberto(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 font-medium text-sm",
                        isActive(item.href)
                          ? "text-foreground bg-accent/10 border border-accent border-l-4 border-l-accent font-semibold"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent border-l-4 border-l-transparent",
                      )}
                    >
                      <item.icon className={cn("w-5 h-5", isActive(item.href) ? "text-accent" : "text-muted-foreground")} />
                      {item.label}
                    </Link>
                  ))}
                </nav>
              )}

              {allUnitsMode && (
                <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
                  <Link
                    href="/"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 font-medium text-sm",
                      "text-foreground bg-accent/10 border border-accent border-l-4 border-l-accent font-semibold",
                    )}
                  >
                    <LayoutDashboard className="w-5 h-5 text-accent" />
                    Dashboard
                  </Link>
                  <Link
                    href="/configuracoes"
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 font-medium text-sm",
                      isActive("/configuracoes")
                        ? "text-foreground bg-accent/10 border border-accent border-l-4 border-l-accent font-semibold"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground border-l-2 border-transparent",
                    )}
                  >
                    <Settings className="w-5 h-5" />
                    Configurações
                  </Link>
                </nav>
              )}
            </>
          )}
        </aside>
        <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-card border-b border-border z-30 flex items-center justify-between px-4">
          <MobileUnitSwitcher
            units={units}
            currentStoreId={user.store.id}
            storeName={user.store.name}
            logoUrl={user.store.logoUrl}
            isPro={isPro}
            allUnitsMode={allUnitsMode}
            onToggleAllUnits={handleToggleAllUnits}
          />
          <div className="flex items-center gap-1">
            {allUnitsMode && location === "/configuracoes" && (
              <Link href="/">
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <LayoutDashboard className="w-4 h-4" />
                </Button>
              </Link>
            )}
            <Link href="/configuracoes">
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                <Settings className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
        {!allUnitsMode && moreMenuOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMoreMenuOpen(false)}>
            <div
              className="absolute bottom-20 left-4 right-4 bg-card rounded-2xl border shadow-xl p-4 space-y-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-muted-foreground">Mais opções</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMoreMenuOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {/* Overflow do main: renderizado primeiro no Mais, mas só
                  aparece em telas < 420px (no mobile-largo eles ficam na nav
                  principal). Em > 420px, "min-[420px]:hidden" os esconde
                  desse menu pra não duplicar com a nav de baixo. */}
              {mobileBottomItems.moreOverflow.map((item) => (
                <Link
                  key={`overflow-${item.href}`}
                  href={item.href}
                  onClick={() => setMoreMenuOpen(false)}
                  className={cn(
                    "flex min-[420px]:hidden items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-medium text-sm",
                    isActive(item.href)
                      ? "text-foreground bg-accent/10 border border-accent font-semibold"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
              {mobileBottomItems.more.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setMoreMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-medium text-sm",
                    isActive(item.href)
                      ? "text-foreground bg-accent/10 border border-accent font-semibold"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
        {!allUnitsMode && (
          <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-card border-t border-border z-30 flex items-center justify-around px-2 safe-area-bottom">
            {mobileBottomItems.main.map((item) => {
              const isOverflow = mobileMainTabsOverflow.includes(item.label);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex-col items-center justify-center gap-0.5 px-2 py-1 rounded-lg min-w-14 transition-colors",
                    // Items "overflow" só aparecem >= 420px de largura; em
                    // telas menores eles vão pro menu "Mais" (renderizado
                    // logo abaixo com responsive reverso).
                    isOverflow ? "hidden min-[420px]:flex" : "flex",
                    isActive(item.href) ? "text-accent bg-accent/10 border border-accent font-semibold" : "text-muted-foreground",
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                </Link>
              );
            })}
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-2 py-1 rounded-lg min-w-[56px] transition-colors",
                moreMenuOpen ? "text-foreground bg-accent/10 border border-accent font-semibold" : "text-muted-foreground",
              )}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium leading-tight">Mais</span>
            </button>
          </nav>
        )}
        <main className={cn(
          "flex-1 w-full pt-14 md:pt-0",
          menuRecolhivel ? "md:pl-[72px]" : "md:pl-64",
          allUnitsMode ? "pb-4 md:pb-0" : "pb-20 md:pb-0",
        )}>
          {(user as any).isImpersonating && !naCentral && (
            <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
              <span className="font-medium">Visualizando a loja: {user.store.name}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-amber-600 gap-1.5"
                onClick={async () => {
                  await customFetch("/api/admin/results/stop-impersonate", { method: "POST" });
                  queryClient.invalidateQueries();
                  setLocation("/results");
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar à minha conta
              </Button>
            </div>
          )}
          <FaixaLojaPausada />
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-in fade-in duration-300">
            {children}
          </div>
        </main>
      </div>
    </AllUnitsContext.Provider>
  );
}
